import { EuiDescribedFormGroup, EuiFieldText, EuiFormRow, EuiPanel, EuiSelect, EuiSelectOption } from "@elastic/eui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FormProvider, SubmitErrorHandler, SubmitHandler, useForm, useFormContext } from "react-hook-form";
import NumberFormat from "react-number-format";

import { ACVEstimatorSchema } from "../schemas/ACVEstimator.schema";
import { ACVEInput } from "./ACVEstimator.types";
import { reportEstimate } from "./calculator";

export const defaultValues: ACVEInput = {
  zip: undefined,
  constructionYear: undefined,
  constructionType: "F",
  squareFootage: 0,
  numFamilies: 1,
};
const ACVEstimator = ({ onFinishEstimator, initialData }: any) => {
  const {
    formState: { errors },
    handleSubmit,
    setValue,
  } = useFormContext<ACVEInput>();

  if (initialData !== undefined) {
    setValue("zip", initialData.zip);
    setValue("constructionYear", initialData.constructionYear);
    setValue("constructionType", initialData.constructionType);
    setValue("numFamilies", initialData.numFamilies);
    setValue("squareFootage", initialData.squareFootage);
  }

  const onSubmit: SubmitHandler<ACVEInput> = (data) => {
    const { totalCoverageALimit } = reportEstimate(data);
    if (onFinishEstimator) {
      onFinishEstimator(data, totalCoverageALimit);
    }
  };
  const onError: SubmitErrorHandler<ACVEInput> = (errors: any) => {
    console.log("ACV submit blocked by validation", errors);
  };

  return (
    <form id="acvestimator" onSubmit={handleSubmit(onSubmit, onError)}>
      <EuiPanel hasBorder>
        <EuiDescribedFormGroup
          title={<h3>Dwelling Description</h3>}
          description=""
          className="wcicRaterDescribedFormGroup"
        >
          <EuiFormRow
            fullWidth
            label="Construction Year"
            isInvalid={!!errors?.constructionYear}
            error={errors.constructionYear?.message}
          >
            <NumberInput name={`constructionYear`} thousandSeparator={false} isInvalid={!!errors?.constructionYear} />
          </EuiFormRow>

          <EuiFormRow
            fullWidth
            label="Construction Type"
            isInvalid={errors.constructionType && true}
            error={errors.constructionType?.message}
          >
            <SelectInput
              name={`constructionType`}
              options={[
                { value: "F", text: "Frame" },
                { value: "M", text: "Masonry" },
              ]}
            />
          </EuiFormRow>

          <EuiFormRow fullWidth label="Zip" isInvalid={errors.zip && true} error={errors.zip?.message}>
            <NumberInput name={`zip`} thousandSeparator={false} isInvalid={errors.zip && true} />
          </EuiFormRow>
          <EuiFormRow
            fullWidth
            label="Number of Families"
            isInvalid={!!errors?.numFamilies}
            error={errors.numFamilies?.message}
          >
            <SelectInput
              name={`numFamilies`}
              options={[
                { value: 1, text: "1" },
                { value: 2, text: "2" },
                { value: 3, text: "3" },
                { value: 4, text: "4+" },
              ]}
            />
          </EuiFormRow>
          <EuiFormRow
            fullWidth
            label="Square Footage"
            isInvalid={errors.squareFootage && true}
            error={errors.squareFootage?.message}
          >
            <NumberInput name={`squareFootage`} isInvalid={errors.squareFootage && true} />
          </EuiFormRow>
        </EuiDescribedFormGroup>
      </EuiPanel>
    </form>
  );
};

export default ACVEstimator;

const NumberInput = ({
  name,
  decimalScale = 0,
  thousandSeparator = true,
  append,
  isInvalid = false,
  onBlurCustom = () => {},
}: {
  name: string;
  decimalScale?: number;
  thousandSeparator?: boolean;
  append?: string;
  isInvalid?: boolean;
  onBlurCustom?: () => void;
}) => {
  return (
    <Controller
      name={`${name}` as const}
      // rules={rules}
      render={({ field }) => {
        const { onChange, value, ref, onBlur, ...rest } = field;
        return (
          <NumberFormat
            customInput={EuiFieldText}
            fullWidth
            thousandSeparator={thousandSeparator}
            decimalScale={decimalScale}
            append={append}
            value={value}
            onValueChange={(target) => {
              if (target.value === "") {
                onChange(null);
              } else {
                onChange(parseInt(target.value, 10));
              }
            }}
            onBlur={(event: any) => {
              if (event.target.value === "") {
                onChange(0);
              }
              onBlur();
              onBlurCustom();
            }}
            {...rest}
            inputRef={ref}
            isInvalid={isInvalid}
          />
        );
      }}
    />
  );
};

const SelectInput = ({
  name,
  placeholder,
  options,
}: {
  name: string;
  placeholder?: string;
  options: EuiSelectOption[];
}) => {
  return (
    <Controller
      name={`${name}` as const}
      render={({ field }) => {
        const { onChange, value, ref, ...rest } = field;

        // Handle React warning safely if value becomes NaN due to an external side-effect
        const safeValue = value === null || value === undefined || Number.isNaN(value) ? "" : value;

        return (
          <EuiSelect
            fullWidth
            options={options}
            value={safeValue}
            onChange={(e) => {
              const selectedValue = e.target.value;

              // Only parse to an integer if the string is purely numeric digits
              if (/^\d+$/.test(selectedValue)) {
                onChange(parseInt(selectedValue, 10));
              } else {
                onChange(selectedValue);
              }
            }}
            {...rest}
            inputRef={ref}
          />
        );
      }}
    />
  );
};

export const RCEstimatorFormProvider = ({ defaultInputValues, children }: any) => {
  const methods = useForm<ACVEInput>({
    mode: "onTouched",
    resolver: yupResolver(ACVEstimatorSchema),
    defaultValues: {
      ...defaultValues,
      ...defaultInputValues,
    },
  });
  return <FormProvider {...methods}>{children}</FormProvider>;
};
